import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Leaf, ArrowRight, Lock, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState([]);

  useEffect(() => { if (user) nav("/"); }, [user, nav]);
  useEffect(() => {
    api.get("/auth/demo-accounts").then(r => setDemo(r.data.accounts || [])).catch(()=>{});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim().toLowerCase(), pwd);
      nav("/");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const useDemo = (a) => { setEmail(a.email); setPwd(a.password); };

  return (
    <div className="min-h-screen w-full bg-white flex">
      {/* Left: brand */}
      <div className="hidden lg:flex w-1/2 relative border-r border-border">
        <div className="absolute inset-0 topo-bg" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-brand text-white flex items-center justify-center">
              <Leaf size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight">ITBPM</div>
              <div className="text-xs text-ink-soft">ESG Intelligence Platform</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="section-label mb-3">Double Materiality · IT/BPM Sector</div>
            <h1 className="text-4xl font-bold tracking-tight leading-[1.05] text-ink">
              The audit-grade ESG operating system for the IT/BPM industry.
            </h1>
            <p className="mt-5 text-sm text-ink-soft leading-relaxed">
              Collect, validate and report carbon, talent, privacy and AI-readiness metrics
              aligned to IFRS S1/S2, GRI, SASB, GHG Protocol and ISO 14001 / 27001 / 45001.
              Multi-tenant. Evidence-backed. AI-assisted.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
              {[
                { l: "17", s: "KPIs in catalogue" },
                { l: "5", s: "Standards mapped" },
                { l: "8", s: "Material issues" },
              ].map((x) => (
                <div key={x.s} className="card p-4">
                  <div className="metric-value text-brand">{x.l}</div>
                  <div className="text-ink-soft mt-1">{x.s}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-ink-soft flex items-center gap-2">
            <ShieldCheck size={14} /> Database-per-tenant isolation · Encrypted in transit & at rest
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="section-label">Sign in</div>
          <h2 className="text-2xl font-semibold mt-1">Access your ESG workspace</h2>
          <p className="text-sm text-ink-soft mt-1">Use one of the demo roles below to explore the platform.</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            <div>
              <label className="dlabel">Email</label>
              <div className="mt-1 flex items-center gap-2 border border-border rounded-md px-3 py-2 focus-within:border-brand">
                <Mail size={14} className="text-ink-mute" />
                <input
                  data-testid="login-email-input"
                  type="email" autoComplete="email" required
                  className="flex-1 outline-none text-sm bg-transparent"
                  value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="dlabel">Password</label>
              <div className="mt-1 flex items-center gap-2 border border-border rounded-md px-3 py-2 focus-within:border-brand">
                <Lock size={14} className="text-ink-mute" />
                <input
                  data-testid="login-password-input"
                  type="password" required autoComplete="current-password"
                  className="flex-1 outline-none text-sm bg-transparent"
                  value={pwd} onChange={e=>setPwd(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-status-error border border-red-200 bg-red-50 rounded-md px-3 py-2">{err}</div>
            )}
            <button
              data-testid="login-submit-button"
              disabled={busy}
              className="btn btn-primary w-full justify-center"
              type="submit"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
            </button>
          </form>

          <div className="mt-8">
            <div className="section-label">Demo accounts</div>
            <div className="mt-2 card overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <table className="dense">
                  <thead>
                    <tr><th>Role</th><th>Tenant</th><th>Email</th><th></th></tr>
                  </thead>
                  <tbody>
                    {demo.map((a, i) => (
                      <tr key={a.email}>
                        <td className="font-medium">{a.role}</td>
                        <td className="text-ink-soft">{a.tenant}</td>
                        <td className="font-mono text-xs">{a.email}</td>
                        <td className="text-right">
                          <button
                            data-testid={`demo-use-${i}`}
                            onClick={()=>useDemo(a)}
                            className="btn btn-ghost text-xs"
                          >Use</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-ink-soft mt-2">All demo passwords end with <span className="font-mono">@123</span>.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
